import React, { useState, useEffect, forwardRef, useRef } from 'react'
import PropTypes from 'prop-types'
import { useGlobalId, useUpdateRef } from '@zambezi/sdk/common'
import { Checkbox } from '@zambezi/sdk/form-elements'
import { getError, errorOptionsType, hintOptionsType } from './zambezi-helpers'
import Label from './Label'

/**
 * @type {React.ForwardRefRenderFunction<React.Ref<import('@zambezi/sdk/form-elements/interfaces').RadioButtonRef>, import('./interfaces').CheckboxFieldProps>}
 * @param {import('./interfaces').CheckboxFieldProps} props
 * @param {React.Ref<import('@zambezi/sdk/form-elements/interfaces').RadioButtonRef>} innerRef
 */
function checkboxField (props, innerRef) {
  const {
    id: propsId,
    name,
    value,
    label,
    className,
    checked,
    rules,
    error,
    errorOptions,
    errorMessageShown,
    disabled,
    hint,
    hintOptions,
    onChange,
    onFocus,
    onBlur,
    inputProps = {},
    errorInline,
    withAsterisk
  } = props

  const errorId = useGlobalId('zb-formidable-error-')
  const globalId = useGlobalId('zb-control-id-')
  const id = propsId || globalId
  const [isChecked, setChecked] = useState(checked || false)
  const elementRef = useRef()
  const inputOptions = {
    ...inputProps,
    className,
    value,
    rules,
    ref: elementRef,
    type: 'checkbox',
    error: !!error,
    checked: isChecked,
    onChange: handleChange,
    onBlur: handleBlur,
    onFocus: handleFocus
  }
  const wrapperClass = 'zb-control-wrap'
  const labelOptions = {
    inputOptions: {},
    hint,
    hintOptions,
    label,
    error,
    errorOptions
  }
  const errorMessage = errorMessageShown(error)
    ? getError(error, errorOptions, errorId, errorInline)
    : null

  if (disabled) inputOptions.disabled = disabled
  if (name) inputOptions.name = name
  if (id) inputOptions.id = id
  if (errorMessage) inputOptions['aria-describedby'] = errorMessage.props.id

  useEffect(() => setChecked(checked), [checked])

  useUpdateRef(innerRef, elementRef, { ...props })

  return (
    <div className={wrapperClass}>
      <Label {...labelOptions}>
        <Checkbox {...inputOptions} />
        {label} {withAsterisk && <sup className='zb-asterisk'>*</sup>}
      </Label>
      {errorMessage}
    </div>
  )

  function handleChange (event, checked) {
    setChecked(checked)
    onChange(name, checked)
  }

  function handleBlur ({ target }) {
    onBlur(target.name, isChecked)
  }

  function handleFocus ({ target }) {
    onFocus(target.name, isChecked)
  }
}

function noop () {}

const CheckboxField = forwardRef(checkboxField)

CheckboxField.displayName = 'CheckboxField'

CheckboxField.propTypes = {
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  error: PropTypes.any,
  errorOptions: errorOptionsType,
  errorMessageShown: PropTypes.func,
  name: PropTypes.string.isRequired,
  id: PropTypes.string,
  value: PropTypes.any,
  checked: PropTypes.bool,
  defaultValue: PropTypes.any,
  className: PropTypes.string,
  label: PropTypes.node,
  disabled: PropTypes.bool,
  hint: PropTypes.string,
  hintOptions: hintOptionsType,
  inputProps: PropTypes.object,
  errorInline: PropTypes.bool,
  withAsterisk: PropTypes.bool
}

CheckboxField.defaultProps = {
  type: 'checkbox',
  value: '',
  withAsterisk: false,
  onFocus: noop,
  onChange: noop,
  onBlur: noop,
  errorMessageShown: (error) => Boolean(error)
}

export default CheckboxField
